import { Box, Button, capitalize, FormControl, IconButton, InputLabel, MenuItem, Select as MuiSelect, TextField as MuiTextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUnteraufgabenHandling } from '../../hooks/unteraufgaben/useUnteraufgabenHandling';
import { Antworttyp } from '../../api';
import { getInputTypeForAntworttyp, getInputPropsForAntworttyp, supportsExpectedValues } from '../../utils/antworttyp';
import { FieldArray, useFormikContext } from 'formik';
import { AntwortTyp } from '../../models/Antworttypen';

export interface ErwartungswertFormData {
    comparator: '<' | '>' | '=';
    expectedAnswer: string;
}

interface ErwartungswerteFieldsProps {
    reihenfolge: number;
    antworttyp: Antworttyp;
    canEditFields: boolean;
}

export default function ErwartungswerteFields({
    reihenfolge,
    antworttyp,
    canEditFields
}: ErwartungswerteFieldsProps) {
    const { translation } = useUnteraufgabenHandling();
    const { values, setFieldValue } = useFormikContext<any>();

    // Only show for types that support expected values
    if (!supportsExpectedValues(antworttyp)) {
        return null;
    }

    const fieldName = `unteraufgaben.${reihenfolge}.erwartungswerte`;
    const erwartungswerte: ErwartungswertFormData[] = values.unteraufgaben?.[reihenfolge]?.erwartungswerte || [];

    const getComparatorLabel = (comparator: '<' | '>' | '=') => {
        switch (comparator) {
            case '<':
                return capitalize(translation('comparator-less-than'));
            case '>':
                return capitalize(translation('comparator-greater-than'));
            case '=':
                return capitalize(translation('comparator-equals'));
        }
    };

    return (
        <Grid size={12}>
            <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: (theme) => theme.typography.fontWeightBold }}>
                    {capitalize(translation('expected-values'))}
                </Typography>

                <FieldArray name={fieldName}>
                    {({ push, remove }) => (
                        <>
                            {erwartungswerte.map((erwartungswert, index) => (
                                <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                                    <Grid size={3}>
                                        <FormControl fullWidth size="small">
                                            <MuiSelect
                                                value={erwartungswert.comparator}
                                                onChange={(e) => {
                                                    setFieldValue(`${fieldName}.${index}.comparator`, e.target.value);
                                                }}
                                                disabled={!canEditFields || antworttyp === AntwortTyp.BOOLEAN}
                                            >
                                                {antworttyp !== AntwortTyp.BOOLEAN && <MenuItem value="<">{getComparatorLabel('<')}</MenuItem>}
                                                {antworttyp !== AntwortTyp.BOOLEAN && <MenuItem value=">">{getComparatorLabel('>')}</MenuItem>}
                                                <MenuItem value="=">{getComparatorLabel('=')}</MenuItem>
                                            </MuiSelect>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={8}>
                                        {antworttyp === AntwortTyp.BOOLEAN ? (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>{capitalize(translation('expected-value-value'))}</InputLabel>
                                                <MuiSelect
                                                    value={erwartungswert.expectedAnswer}
                                                    onChange={(e) => {
                                                        setFieldValue(`${fieldName}.${index}.expectedAnswer`, e.target.value);
                                                    }}
                                                    label={capitalize(translation('expected-value-value'))}
                                                    disabled={!canEditFields}
                                                >
                                                    <MenuItem value="true">{capitalize(translation('boolean-true'))}</MenuItem>
                                                    <MenuItem value="false">{capitalize(translation('boolean-false'))}</MenuItem>
                                                </MuiSelect>
                                            </FormControl>
                                        ) : (
                                            <MuiTextField
                                                value={erwartungswert.expectedAnswer}
                                                onChange={(e) => {
                                                    setFieldValue(`${fieldName}.${index}.expectedAnswer`, e.target.value);
                                                }}
                                                type={getInputTypeForAntworttyp(antworttyp)}
                                                inputProps={getInputPropsForAntworttyp(antworttyp)}
                                                label={capitalize(translation('expected-value-value'))}
                                                fullWidth
                                                size="small"
                                                disabled={!canEditFields}
                                            />
                                        )}
                                    </Grid>
                                    <Grid size={1}>
                                        <IconButton
                                            onClick={() => remove(index)}
                                            size="small"
                                            disabled={!canEditFields}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}

                            {canEditFields && (
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        const defaultValue = antworttyp === AntwortTyp.BOOLEAN ? 'true' : '';
                                        push({ comparator: '=', expectedAnswer: defaultValue });
                                    }}
                                    size="small"
                                    variant="outlined"
                                >
                                    {capitalize(translation('expected-value-add'))}
                                </Button>
                            )}
                        </>
                    )}
                </FieldArray>
            </Box>
        </Grid>
    );
}

